import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../../components/dashboard/Sidebar";
import { BsSuitHeartFill, BsSuitHeart } from "react-icons/bs";
import { MdLocationOn, MdEdit, MdDelete } from "react-icons/md";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import AOS from "aos";
import "aos/dist/aos.css";

import Cairo from "../../../assets/images/CairoHotel.jfif";
import lux from "../../../assets/images/lux.jpeg";
import elgouna from "../../../assets/images/elgouna.jpeg";
import dahab from "../../../assets/images/dahab.jpeg";
import nuwiba from "../../../assets/images/nwee.webp";

gsap.registerPlugin(ScrollTrigger);

const Hotels = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([
    { id: 1, img: Cairo, name: "Cairo, Egypt", price: "44.00", badge: true },
    { id: 2, img: lux, name: "Luxor, Egypt", price: "250.00", badge: true },
    { id: 3, img: elgouna, name: "El Gouna, Egypt", price: "80.00", badge: true },
    { id: 4, img: dahab, name: "Dahab, Egypt", price: "60.00", badge: false },
    { id: 5, img: nuwiba, name: "Nuweiba, Egypt", price: "60.00", badge: false },
  ]);

  const [wishList, setWishList] = useState([]);
  const [newHotel, setNewHotel] = useState({ name: "", price: "", img: "" });
  const [editHotel, setEditHotel] = useState(null);

  const handleWishList = (hotel) => {
    if (wishList.some((item) => item.id === hotel.id)) {
      setWishList(wishList.filter((item) => item.id !== hotel.id));
      toast.info("Removed from Wish List");
    } else {
      setWishList([...wishList, hotel]);
      toast.success("Added to Wish List");
    }
  };

  const handleDeleteHotel = (id) => {
    setHotels(hotels.filter((hotel) => hotel.id !== id));
    toast.error("Hotel Deleted");
  };

  const handleAddHotel = () => {
    if (!newHotel.name || !newHotel.price || !newHotel.img) {
      toast.warning("Please fill in all fields", {
        style: { background: "#E6C78A", color: "#000" },
      });
      return;
    }
    if (editHotel) {
      setHotels(
        hotels.map((hotel) =>
          hotel.id === editHotel.id ? { ...editHotel, ...newHotel } : hotel
        )
      );
      setEditHotel(null);
      toast.success("Hotel Updated Successfully");
    } else {
      const newId = hotels.length + 1;
      setHotels([...hotels, { ...newHotel, id: newId, badge: false }]);
      toast.success("Hotel Added Successfully");
    }
    setNewHotel({ name: "", price: "", img: "" });
  };

  const handleEditHotel = (hotel) => {
    setNewHotel(hotel);
    setEditHotel(hotel);
  };

  useEffect(() => {
    AOS.init({ duration: 800 });
  }, []);

  useEffect(() => {
    gsap.utils.toArray(".hotel-item").forEach((element) => {
      gsap.fromTo(
        element,
        { opacity: 0, y: 80, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.8,
          ease: "power3.out",
          stagger: 0.25,
          scrollTrigger: {
            trigger: element,
            start: "top 90%",
            end: "top 30%",
            scrub: true,
            toggleActions: "play none none reverse",
          },
        }
      );
    });
  }, []);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8">
        <div className="container mx-auto">
          {/* Form inputs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6 max-w-full">
            <input
              type="text"
              placeholder="Hotel Name"
              className="border p-3 rounded-md w-full sm:w-1/3 text-sm focus:outline-none"
              value={newHotel.name}
              onChange={(e) => setNewHotel({ ...newHotel, name: e.target.value })}
            />
            <input
              type="text"
              placeholder="Price"
              className="border p-3 rounded-md w-full sm:w-1/5 text-sm focus:outline-none"
              value={newHotel.price}
              onChange={(e) => setNewHotel({ ...newHotel, price: e.target.value })}
            />
            <input
              type="text"
              placeholder="Image URL"
              className="border p-3 rounded-md w-full sm:w-2/5 text-sm focus:outline-none"
              value={newHotel.img}
              onChange={(e) => setNewHotel({ ...newHotel, img: e.target.value })}
            />
            <button
              onClick={handleAddHotel}
              className="bg-[#E6C78A] text-white px-6 py-3 rounded-md text-sm transition-transform ease-out hover:scale-105 whitespace-nowrap"
            >
              {editHotel ? "Update Hotel" : "Add Hotel"}
            </button>
          </div>

          {/* Hotels Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
            {hotels.map((hotel) => (
              <motion.div
                key={hotel.id}
                className="hotel-item relative group border border-gray-300 rounded-xl shadow-md bg-white transition-transform duration-300 transform hover:scale-105 hover:shadow-lg"
                data-aos="fade-up"
              >
                <div className="relative w-full h-48 sm:h-52 md:h-56 lg:h-60 overflow-hidden rounded-t-xl">
                  <img
                    className="w-full h-full object-cover"
                    src={hotel.img}
                    alt={hotel.name}
                    loading="lazy"
                  />
                  {/* Featured badge */}
                  {hotel.badge && (
                    <span className="absolute top-4 left-4 bg-gray-800 text-white px-2 py-1 rounded text-xs select-none">
                      Featured
                    </span>
                  )}
                  {/* Wishlist heart */}
                  <div className="absolute top-4 right-4 cursor-pointer z-10">
                    {wishList.some((item) => item.id === hotel.id) ? (
                      <BsSuitHeartFill
                        size={26}
                        className="text-red-500"
                        onClick={() => handleWishList(hotel)}
                      />
                    ) : (
                      <BsSuitHeart
                        size={26}
                        className="text-gray-500"
                        onClick={() => handleWishList(hotel)}
                      />
                    )}
                  </div>
                </div>
                <div className="p-4 sm:p-5">
                  <h2 className="text-lg sm:text-xl font-semibold mb-2 flex items-center gap-2 text-gray-800">
                    <MdLocationOn size={20} className="text-[#46A29F]" />
                    {hotel.name}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600">
                    Price: <span className="font-semibold">${hotel.price}</span> per night
                  </p>
                  <div className="flex justify-between mt-4">
                    <button
                      className="flex items-center gap-2 px-3 py-2 bg-[#46A29F] text-white rounded-md text-sm transition-transform duration-300 hover:scale-105 whitespace-nowrap"
                      onClick={() => handleEditHotel(hotel)}
                    >
                      <MdEdit className="mr-1" /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteHotel(hotel.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-md text-sm transition-transform duration-300 hover:scale-105 whitespace-nowrap"
                    >
                      <MdDelete className="mr-1" /> Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Hotels;
